import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import { getBrokerConditionSchema } from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionsForm.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';

import { getLoanSpecificForms } from 'store/forms/actions';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addBrokerConditionAction,
  addInternalConditionAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField, selectTaskTypesOptions } from 'store/system/selectors';
import { selectPriorTo, selectSlaGroupsOptions } from 'store/underwriting/selectors';

import AdhocConditionFields from '../components/AdhocConditionFields';
import { ConditionsTestIds } from '../Conditions.testIds';

import {
  initialBrokerFormValues,
  initialInternalFormValues,
} from './AddBrokerConditionForm.constants';
import { footerStyles, formWrapperStyles } from './AddBrokerConditionForm.styles';
import {
  applyUploadedFormToTask,
  getCreateFormDialogProps,
  getFormViewerDialogProps,
  prepareBrokerConditionPayload,
  prepareInternalConditionRequest,
} from './AddBrokerConditionForm.utils';

const AddBrokerConditionForm = ({
  formName = FormNames.ADD_CONDITION,
  customOnAssignFormClick = _.noop,
  initialForm = null,
  isInternal = false,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const taskTypeOptions = useSelector(selectTaskTypesOptions);
  const priorToOptions = useSelector(selectPriorTo);
  const slaGroupsOptions = useSelector(selectSlaGroupsOptions);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(formName));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(formName));
  const isPageLocked = useSelector(selectFormLockedPage(formName));

  const isBrokerTasksFeatureEnabled = useFeatureToggle(
    FeatureFlag.EnableBrokerConditionTaskAssignment,
  );
  const isTasksEnabled = isInternal || isBrokerTasksFeatureEnabled;

  const [isLoading, setIsLoading] = useState(false);

  const isDisabledByLock = isLoading || isPageLocked;

  const {
    form,
    isFormChanged,
    setInForm,
    validationErrors,
    setSchema,
    handleChange,
    validateForm,
    validateField,
  } = useForm(initialForm || (isInternal ? initialInternalFormValues : initialBrokerFormValues));

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onCreateNewForm = useCallback(
    ({ taskIndex }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const newForm = applyUploadedFormToTask({ form, taskIndex, uploadedForm });
            const onAssignClick = () => customOnAssignFormClick(newForm);

            dispatch(openDialogAction(getFormViewerDialogProps({ uploadedForm, onAssignClick })));
          });
      };

      dispatch(
        openDialogAction(
          getCreateFormDialogProps({ abortController, applicationId, onFinishFormUploading }),
        ),
      );
    },
    [applicationId, customOnAssignFormClick, dispatch, form, loanId],
  );

  const onCancel = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const refreshConditions = useCallback(() => {
    const fetchConditions = isInternal
      ? getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true })
      : getLoanBrokerConditionByApplicationIdAction({ applicationId });

    return Promise.all([
      dispatch(fetchConditions),
      dispatch(getLoanApplicationTasksAction({ applicationId })),
    ]);
  }, [applicationId, dispatch, isInternal, loanId]);

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    setIsLoading(true);

    const createAction = isInternal
      ? addInternalConditionAction(
          prepareInternalConditionRequest({
            form,
            applicationId,
            loanId,
            taskTypeDynamicFieldOptions,
          }),
        )
      : addBrokerConditionAction({
          applicationId,
          loanConditions: [
            prepareBrokerConditionPayload({
              form,
              isBrokerTasksEnabled: isBrokerTasksFeatureEnabled,
              loanId,
              taskTypeDynamicFieldOptions,
            }),
          ],
        });

    dispatch(createAction)
      .then(refreshConditions)
      .then(() => {
        dispatch(
          showMessageAction({ message: 'Condition successfully added.', variant: 'success' }),
        );
      })
      .catch(Error)
      .finally(() => {
        dispatch(closeDialogAction());
        setIsLoading(false);
      });
  }, [
    applicationId,
    dispatch,
    form,
    isBrokerTasksFeatureEnabled,
    isInternal,
    loanId,
    refreshConditions,
    taskTypeDynamicFieldOptions,
    validateForm,
  ]);

  useEffect(() => {
    setSchema(
      getBrokerConditionSchema({
        conditionTaskTypes: form.conditionTaskTypes,
        taskTypeDynamicFieldOptions,
        isInternal,
      }),
    );
  }, [setSchema, form.conditionTaskTypes, taskTypeDynamicFieldOptions, isInternal]);

  return (
    <Box>
      <Box sx={formWrapperStyles}>
        <AdhocConditionFields
          form={form}
          formName={formName}
          handleChange={handleChange}
          hiddenFieldsMap={hiddenFieldsMap}
          isDisabledByLock={isDisabledByLock}
          isInternal={isInternal}
          isTasksEnabled={isTasksEnabled}
          lockedFieldsMap={lockedFieldsMap}
          priorToOptions={priorToOptions}
          setInForm={setInForm}
          slaGroupsOptions={slaGroupsOptions}
          taskTypeOptions={taskTypeOptions}
          validateField={validateField}
          validationErrors={validationErrors}
          onAutocompleteChange={onAutocompleteChange}
          onCreateNewForm={onCreateNewForm}
        />
      </Box>

      <Box sx={footerStyles}>
        <Button
          data-testid={ConditionsTestIds.BROKER_FORM_CANCEL_BUTTON}
          disabled={isLoading}
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-testid={ConditionsTestIds.BROKER_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading}
          variant="contained"
          onClick={onSubmit}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

AddBrokerConditionForm.propTypes = {
  formName: PropTypes.string,
  customOnAssignFormClick: PropTypes.func,
  initialForm: PropTypes.object,
  isInternal: PropTypes.bool,
};

export default memo(AddBrokerConditionForm);
